import {
    AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
    Input, ViewChild, ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { isBoolean, isNullOrUndefined, isUndefined } from '../../models/utils';
import { Comparison } from '../../models/comparison';
import { DrEntityAttribute } from '../../models/dr-entity-attribute';
import { Endpoint, EntityType, HelpIndex, MessageType, SearchType, SettingsType } from '../../models/enums';
import { ObjectActionListType, Permissions } from '../../models/permission';
import { Profile } from '../../models/profile';
import { SavedSearch } from '../../models/saved-searches';
import { SearchResult } from '../../models/search-result';
import { TaskType } from '../../models/task';
import { ToggleItem } from '../../models/toggle-item';
import { Workflow, WorkflowStep } from '../../models/workflow';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { BulkSearchComponent } from '../bulk-search/bulk-search.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { TargetPickerModalComponent } from '../modals/target-picker-modal/target-picker-modal.component';
import { TargetPickerTreeType } from '../target-picker-tree/target-picker-tree.component';
import { AdvancedSearchModalComponent } from './advanced-search-modal/advanced-search-modal.component';
import { GenericInputComponent } from '../generic-input/generic-input.component';
import { Corpus } from '../../models/corpus';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { MAX_LIMIT_OF_RENDERED_QUERY } from '../../models/constants';
import { MetadataService, MetadataToken } from '../../services/metadata/metadata.service';

export class AdvancedSearchBundle {
    query: string;
    xmlQuery: string;
    queryObject: any;

    constructor(value?: Partial<WorkflowStep>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

@Component({
    standalone: false,
    selector: 'res-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SearchComponent implements AfterViewInit {
    @Input() workflowMode = false;
    @ViewChild(BulkSearchComponent) bulkSearchComponent: BulkSearchComponent;
    @ViewChild(GenericInputComponent) genericInputComponent: GenericInputComponent;
    automationId = 'search-panel';
    SearchType = SearchType;

    searchTypeChangedEvent: EventEmitter<{ searchType: SearchType, offset?: number, keepSelection?: boolean, resetSearchSize?: boolean }> = new EventEmitter();
    searchType: SearchType;
    currentSearch: SearchResult;
    queryString = '';
    isQueryStringDisplayedTrimmed = false;
    advancedQuery = new AdvancedSearchBundle();
    corporaFilters: Array<any> = [];
    corporaFilterTooltip = '';
    dataAreaHandle = '';
    freeFormToggles = [
        new ToggleItem({
            label: 'Include Families',
            isChecked: true
        }),
        new ToggleItem({
            label: 'Include Metadata',
            isChecked: true
        }),
        new ToggleItem({
            label: 'Generate Reports',
        })
    ];

    advancedToggles = [
        new ToggleItem({
            label: 'Include Families',
            isChecked: true
        }),
        new ToggleItem({
            label: 'Include Metadata',
            isChecked: true
        }),
        new ToggleItem({
            label: 'Expand Synonyms',
        }),
        new ToggleItem({
            label: 'Generate Reports',
        })
    ];

    target: any;
    totalWidth: number;
    toggleWidth = 31;
    isExpanded = false;
    isSearchBusy: boolean;
    searchBusyMessage: string;
    disabledAdvanceSearch = false;
    metadataList: Array<MetadataToken> = [];

    private cancelBulkSearch: Subscription;

    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
        this.checkWidth();
    }

    resizeCallback = (): void => {
        this.contextMenuComponent.closeAllContextMenus();
        this.checkWidth();
    }

    constructor(private elemRef: ElementRef, private contextMenuComponent: ContextMenuComponent, private commonService: CommonService, private restService: RestService, private cdr: ChangeDetectorRef,
        private xmlService: XMLService, private metadataService: MetadataService) {
        this.target = {
            name: 'Project Data',
        };

        (<any>window).hideBusySpinner = () => {
            this.hideBusySpinner();  // from an external window
        };

        this.cancelBulkSearch = this.commonService.getMessage(MessageType.cancelBulkSearch).subscribe((data) => {
            this.hideBusySpinner();
        });

        this.metadataService.tokenizationTypes().then(responseTokenizations => {
            this.metadataList = responseTokenizations;
        });
    }

    ngAfterViewInit(): void {
            this.setType(SearchType.FREEFORM);
        setTimeout(() => {
            this.checkWidth();
        });
        this.cdr.detectChanges();
    }

    projectWasLoaded(): boolean {
        return !isNullOrUndefined(this.commonService.projectData) && !isNullOrUndefined(this.commonService.currentProject);
    }

    isCurrentResultsEnabled(): boolean {
        return this.currentSearch !== undefined && this.currentSearch.task !== SearchType.BULK;
    }

    launchPicker(): void {
        this.commonService.openModal(TargetPickerModalComponent, {
            modalData: {
                targetPickerTreeType: TargetPickerTreeType.selectSearchTarget, dataSetFilter: this.corporaFilters,
                initialSelectedNodeData: this.target
            },
            class: 'horizontal-px-700 vertical-percent-80'
        }).then(response => {
            if (response && response.modalResult && response.modalData) {
                const selTarget = response.modalData.target;
                if (selTarget && selTarget.data) {
                    const isDataSet = selTarget.data instanceof Corpus && selTarget.data.corpusViewHandle;
                    const targetHandle = isDataSet ? selTarget.data.corpusViewHandle : selTarget.data.handle;
                    this.target = {
                        name: selTarget.data instanceof WorkflowStep ? selTarget.data.description : selTarget.data.name,
                        handle: selTarget.data.view ? selTarget.data.view.handle : targetHandle,
                        target: selTarget
                    };
                }
                this.corporaFilters = response.modalData.dataSetFilter;
                if (this.corporaFilters && this.corporaFilters.length > 0) {
                    const names = this.corporaFilters.map(function (elem) { return '- ' + elem.name; });
                    this.corporaFilterTooltip = 'Filtered by Data Set/s:\n' + names.join('\n');
                } else {
                    this.corporaFilterTooltip = '';
                }
                if (this.target.name) {
                    this.target.name = this.target.name.replace('CaseData', 'Project Data');
                }
            }
        });
    }

    setTarget(searchData: SearchResult | WorkflowStep): void {
        if (searchData) {
            if (searchData instanceof SearchResult) {
                searchData.name = searchData.name.replace('CaseData', 'Project Data');
            } else {
                searchData.description = searchData.description.replace('CaseData', 'Project Data');
            }
            this.advancedQuery.query = '';
            this.advancedQuery.xmlQuery = '';
            this.advancedQuery.queryObject = null;
            this.target = {
                name: searchData instanceof SearchResult ? searchData.name : searchData.description,
                handle: searchData.handle,
                documentCount: searchData instanceof SearchResult ? searchData.documentCount : searchData.numberResults,
                entityType: EntityType.searchResult
            };
        } else {
            // default to Project Data if it has permissions, otherwise to first searchable entity with permissions
            this.getDefaultSearchTargetWithPermissions();
        }
        this.queryString = '';
        this.genericInputComponent?.setText('');
        this.setCursorAtIndex();
        // 300 to get the Max size expand.
        this.searchTypeChangedEvent.emit({ searchType: this.searchType, offset: 300 });
    }

    parseXMLQuerySearchType(xmlQuery: string): SearchType {
        if (xmlQuery) {
            const query = this.xmlService.xmlToQueryConverter(xmlQuery) as string;
            if (xmlQuery.includes('cookie="DRAdvancedSearch"') && !xmlQuery.includes('cookie="Combined_Search_')) {
                this.searchType = SearchType.ADVANCED;
                this.advancedQuery.xmlQuery = xmlQuery;
                this.advancedQuery.query = query;
                this.queryString = '';
                return SearchType.ADVANCED;
            } else {
                this.queryString = this.xmlService.stripExtraneousParens(query);
                return SearchType.FREEFORM;
            }
        }
    }

    setSearchData(searchData: any): void {
        if (isNullOrUndefined(searchData.searchType)) {
            searchData.searchType = SearchType.FREEFORM;
        }
        this.setType(searchData.searchType);
        if (searchData.searchType === SearchType.FREEFORM) {
            if (isBoolean(searchData.includeFamilyMembers)) {
                this.freeFormToggles.find((item) => item.label === 'Include Families').isChecked = searchData.includeFamilyMembers;
            }
            if (isBoolean(searchData.expandFields)) {
                this.freeFormToggles.find((item) => item.label === 'Include Metadata').isChecked = searchData.expandFields;
            }
            if (searchData.reportRequests?.length > 0) {
                this.freeFormToggles.find((item) => item.label === 'Generate Reports').isChecked = true;
            }
            setTimeout(() => {
                const queryObj = this.xmlService.xmlToQueryConverter(searchData.query, true);
                if (queryObj) {
                    this.queryString = this.xmlService.stripExtraneousParens((queryObj as any).query);
                    const text = this.returnQueryStringToDisplay(this.queryString);
                    this.renderFreeformSearchFromWorkflow(this.isQueryStringDisplayedTrimmed);
                    this.genericInputComponent?.setText(text);
                    this.genericInputComponent.colorize();
                }
            });
        } else if (searchData.searchType === SearchType.ADVANCED) {
            this.advancedToggles.find((item) => item.label === 'Include Families').isChecked = searchData.includeFamilyMembers;
            this.advancedToggles.find((item) => item.label === 'Include Metadata').isChecked = searchData.expandFields;
            this.advancedToggles.find((item) => item.label === 'Expand Synonyms').isChecked = searchData.includeSynonyms;
            if (searchData.reportRequests?.length > 0) {
                this.advancedToggles.find((item) => item.label === 'Generate Reports').isChecked = true;
            }
            const queryObj = this.xmlService.xmlToQueryConverter(searchData.query, true);
            this.advancedQuery.query = (queryObj as any).query;
            this.advancedQuery.xmlQuery = searchData.query;
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode): void {
        let searchData = node.data;
        this.corporaFilters = [];
        let isSampleSet = false;
        if (node.isSelectable) {
            this.target = {};
        }
        if (this.commonService.searchDialect === 'DR_CLASSIC') {
            this.disabledAdvanceSearch = true;
        }
        if (searchData && searchData.attributes && searchData.attributes.length > 0) {
            const sampleAttrib = searchData.attributes.find(a => a.name === 'sampleSetResult');
            isSampleSet = sampleAttrib && sampleAttrib.value;
        }
        if (searchData && searchData.target) {
            this.target = {
                name: searchData.target.name,
                handle: searchData.target.handle,
            };
        }

        const isProfileSearch = searchData instanceof SearchResult && searchData.taskType && searchData.taskType === TaskType.SEARCH_BY_PROFILE;
        if (!isNullOrUndefined(searchData) && !isNullOrUndefined(searchData.step) && !(searchData instanceof AdvancedSearchBundle)) {
            this.setSearchData(searchData.step);
        } else if (this.workflowMode
            || (searchData instanceof SearchResult && searchData.task && !this.isDuplicateSearch(searchData.task, searchData.taskType) && !isProfileSearch && !isSampleSet)
            || searchData instanceof SavedSearch || searchData instanceof AdvancedSearchBundle || searchData instanceof WorkflowStep) {
            if (searchData instanceof AdvancedSearchBundle) {
                this.advancedQuery = searchData;
                searchData = this.advancedQuery.query;
            }
            this.currentSearch = (searchData instanceof SearchResult || searchData instanceof WorkflowStep) ? searchData : searchData.view;
            if (searchData instanceof SearchResult || searchData instanceof WorkflowStep) {
                const searchDataName = searchData instanceof SearchResult ? searchData.name : searchData.description;
                if (searchDataName) {
                    this.queryString = searchDataName.includes('Searching for ') ?
                        this.queryString?.replace('Searching for ', '') : this.queryString?.replace('Searching ', '');
                } else {
                    this.queryString = '';
                    this.advancedQuery.query = '';
                    this.advancedQuery.xmlQuery = '';
                    this.advancedQuery.queryObject = null;
                }
            }

            if (this.searchType === SearchType.ADVANCED) {
                this.advancedQuery.query = this.advancedQuery && this.advancedQuery.query ? this.advancedQuery.query.replace('Metadata Bulk Search for ', '') : '';
            } else {
                this.queryString = this.queryString.replace('Metadata Bulk Search for ', '');
                if (this.queryString.includes('Search by synthetic document')) {
                    this.queryString = '';
                }
            }

            const isOcrSearch = this.currentSearch && this.currentSearch.attributes && (this.currentSearch.attributes.findIndex(a => a.name === 'ocrView') !== -1);
            if (!searchData.step && this.currentSearch && (this.currentSearch.task === SearchType.FREEFORM || this.currentSearch.task === SearchType.SEARCH_FROM_BULK_SEARCH
                || this.currentSearch.task === SearchType.METADATA_BULK || this.currentSearch.task === SearchType.BULK)) {

                if ((this.currentSearch.task === SearchType.METADATA_BULK || this.currentSearch.task === SearchType.BULK) && this.bulkSearchComponent) {
                    this.bulkSearchComponent.clearAllQueries(true);
                    this.bulkSearchComponent.isMetadataSearch = this.currentSearch.task === SearchType.METADATA_BULK;
                }
                this.getSearchInfoFromTask(searchData.handle, isOcrSearch);
            } else if (searchData instanceof SavedSearch) {
                this.clearQueries();
                let newSearchType = SearchType.FREEFORM;
                const queryFromSavedSearch = (searchData as SavedSearch).searchQuery;
                const getQueryWithInfo = this.workflowMode ? (Object)(this.xmlService.xmlToQueryConverter(searchData.xmlQuery, true)) : (Object)(this.xmlService.xmlToQueryConverter(queryFromSavedSearch, true));
                if (getQueryWithInfo.searchType === "DRAdvancedSearch") {
                    newSearchType = SearchType.ADVANCED;
                }
                this.setType(newSearchType);
                if (getQueryWithInfo.searchType === "DRAdvancedSearch") {
                    this.advancedQuery.xmlQuery = (searchData as SavedSearch).searchQuery;
                    this.advancedQuery.query = queryFromSavedSearch && this.workflowMode ? queryFromSavedSearch : this.xmlService.xmlToQueryConverter(queryFromSavedSearch).toString();
                } else {
                    this.queryString = queryFromSavedSearch && this.workflowMode ? queryFromSavedSearch ? queryFromSavedSearch : '' : this.xmlService.xmlToQueryConverter(queryFromSavedSearch).toString();
                }
                const searchOptions = [
                    new DrEntityAttribute({ name: 'includeFamily', value: searchData.includeFamily.toString() }),
                    new DrEntityAttribute({ name: 'includeMetadata', value: searchData.includeMetadata.toString() }),
                    new DrEntityAttribute({ name: 'includeSynonyms', value: searchData.includeSynonyms.toString() }),
                    new DrEntityAttribute({ name: 'generateReport', value: searchData.generateReports.toString() })
                ];
                this.setSearchOptionsFromAttribs(searchOptions);
            } else {
                if (!this.isDuplicateSearch(this.currentSearch.task)) {
                    this.setType(this.currentSearch.task);
                }
            }

            if (!isOcrSearch) {
                this.getSearchTarget(searchData);
            }
            const stripQueryString = this.xmlService.stripExtraneousParens(this.queryString);
            this.queryString = stripQueryString;
            const text = this.returnQueryStringToDisplay(this.queryString);
            this.genericInputComponent?.setText(text);
            this.genericInputComponent.colorize();
            if (this.searchType === SearchType.FREEFORM) {
                this.autosizeFreeformDelayed();
            }
        } else {
            this.genericInputComponent?.enable();
            this.currentSearch = undefined;
            if (!this.searchType) {
                this.setType(SearchType.FREEFORM);
            }
            if (searchData && (searchData instanceof SearchResult) && (this.isDuplicateSearch(searchData.task, searchData.taskType) || isProfileSearch || isSampleSet)) {
                this.clearQueryValueForAllSearchModes();
                if (isSampleSet) {
                    this.target.name = searchData.name;
                    this.target.handle = searchData.handle;
                    this.resetSearchOptionsOfCurrentSearchToDefault();
                } else {
                    // clears all search options since they do not apply
                    this.setSearchOptionsFromAttribs(searchData.attributes);
                    this.getSearchTarget(searchData);
                }
                return;
            }
            const exclusionList = ['Dashboard', 'Custodians', 'Tags', 'Folders', 'Saved Searches', 'Search History',
                'Workflows', 'Workflow', 'Comparisons', 'Profiles', 'Profile', 'MediaIDs', 'Batches'];
            if (_.indexOf(exclusionList, node.name) > -1 || searchData instanceof Comparison || searchData instanceof Profile || node.data instanceof Workflow) {
                this.getDefaultSearchTargetWithPermissions();
                this.clearQueryValueForAllSearchModes();
                this.resetSearchOptionsOfCurrentSearchToDefault();
            } else {
                if (!node.data && ((node.entityType === EntityType.import) || (node.entityType === EntityType.export))) {
                    if (node.entityType === EntityType.import) {
                        this.target = {
                            name: 'Imports',
                            handle: node.requestInfo?.data.corpusSetHandle
                        };
                    } else {
                        this.getDefaultSearchTargetWithPermissions();
                    }
                    this.clearQueryValueForAllSearchModes();
                    this.resetSearchOptionsOfCurrentSearchToDefault();
                } else {
                    if (node.data instanceof WorkflowStep) {
                        const currentTargetName = _.findWhere(node.data.attributes, { name: 'targetname' });
                        const currentTargetHandle = _.findWhere(node.data.attributes, { name: 'targethandle' });
                        this.target = {
                            name: currentTargetName && currentTargetName.value ? currentTargetName.value : '',
                            handle: currentTargetHandle && currentTargetHandle.value ? currentTargetHandle.value : ''
                        };
                        const queryEntered = _.findWhere(node.data.attributes, { name: 'queryEntered' });
                        if (queryEntered) {
                            this.setType(this.parseXMLQuerySearchType(queryEntered.value), true);
                        }
                        this.setSearchOptionsFromAttribs(node.data.attributes, true);
                    } else if (searchData && searchData.name.startsWith('exportcompare_') && node && node.parent) {
                        this.target.handle = node.parent.data.handle;
                        this.target.name = node.parent.name;
                        this.clearQueryValueForAllSearchModes();
                        this.resetSearchOptionsOfCurrentSearchToDefault();
                    } else {
                        if (node.data) {
                            if (node.entityType === EntityType.import && node.data.corpusViewHandle) {
                                this.target.handle = node.data.corpusViewHandle;
                            } else {
                                this.target.handle = node.data.view ? node.data.view.handle : node.data.handle;
                            }
                            this.target.name = node.data.name;
                        }
                        this.clearQueryValueForAllSearchModes();
                        this.resetSearchOptionsOfCurrentSearchToDefault();
                    }
                }
            }
        }
    }

    removeSearchingPrefixFromTargetName(targetName: string): string {
        if (targetName) {
            if (targetName.startsWith('Searching for ')) {
                return targetName.replace('Searching for ', '');
            } else if (targetName.trim().startsWith('Searching ')) {
                return targetName.trim().replace('Searching ', '');
            }
            return targetName;
        }
        return targetName;
    }
    clearQueryValueForAllSearchModes(): void {
        this.queryString = '';
        this.isQueryStringDisplayedTrimmed = false;
        this.genericInputComponent?.setText('');
        if (this.advancedQuery) {
            this.advancedQuery.query = '';
            this.advancedQuery.queryObject = null;
            this.advancedQuery.xmlQuery = '';
        }
        if (this.bulkSearchComponent) {
            this.bulkSearchComponent.clearAllQueries(true);
        }
    }

    clearFreeFormQuery(): void {
        this.queryString = '';
        this.isQueryStringDisplayedTrimmed = false;
        this.genericInputComponent?.setText('');
        this.genericInputComponent?.enable();
        this.setCursorAtIndex();
    }

    setCursorAtIndex() {
        setTimeout(() => {
            if (this.genericInputComponent) {
                const rangeIndex = this.genericInputComponent.getText().length;
                const range = { index: rangeIndex, length: 0 };
                this.genericInputComponent.setSelection(range);
            }
        });
    }

    // Note: For the three main Search Modes (Freeform, Advanced, and Bulk Search),
    // the following Family options are enabled by default: Include Families and Include Metadata
    resetSearchOptionsOfCurrentSearchToDefault(): void {
        const defaultSearchOptions = [
            new DrEntityAttribute({ name: 'includeFamily', value: 'true' }),
            new DrEntityAttribute({ name: 'includeMetadata', value: 'true' }),
            new DrEntityAttribute({ name: 'includeSynonyms', value: 'false' }),
            new DrEntityAttribute({ name: 'generateReport', value: 'false' })
        ];
        if (this.searchType === SearchType.METADATA_BULK) {
            this.searchType = SearchType.BULK;
        }
        this.setSearchOptionsFromAttribs(defaultSearchOptions);
    }

    clearQueries(): void {
        this.queryString = '';
        if (this.advancedQuery) {
            this.advancedQuery.query = '';
        }
        this.genericInputComponent?.setText(this.queryString);
        this.genericInputComponent.colorize();
    }

    // returns true for results of: Find More Like These, Find Exact/Content Duplicates, Find Exact/Content/Near Duplicates of This
    isDuplicateSearch(task: string, taskType: string = null): boolean {
        return task === SearchType.COLLECTION_SIMILARITY || task === SearchType.VECTOR_SET_FIND_DUPLICATES
            || task === SearchType.DOCUMENT_FIND_DUPLICATES || (task === SearchType.FREEFORM && taskType === TaskType.NEAR_DUPE);
    }

    getSearchTarget(searchData: any): void {
        let targetHandle = '', targetName = '', handleForSharedDataSet = '', targetSize = '';
        if (searchData && searchData.attributes && this.commonService.currentProject) {
            let attrib = searchData.attributes.find(a => a.name === 'targetViewHandle' || a.name === 'targethandle');
            targetHandle = attrib ? attrib.value : (searchData.targetViewHandle ? searchData.targetViewHandle : '');
            handleForSharedDataSet = attrib ? this.commonService.currentProject.handle + ':' + attrib.value : '';
            attrib = searchData.attributes.find(a => a.name ? a.name.toLowerCase() === 'targetviewname' || a.name === 'targetname' : false);
            targetName = attrib ? this.removeSearchingPrefixFromTargetName(attrib.value) : '';
            attrib = searchData.attributes.find(a => a.name === 'targetsize');
            targetSize = attrib ? attrib.value : '';
        }
        if (!handleForSharedDataSet || handleForSharedDataSet !== this.target.handle) {
            this.target.handle = targetHandle;
        }
        this.target.name = targetName;
        this.target.name = this.target.name.replace('Case Data', 'Project Data');
        this.target.name = this.target.name.replace('CaseData', 'Project Data');
        this.target.documentCount = targetSize;
        if (!targetName) {
            const index = this.queryString.lastIndexOf(') in ');
            if (index !== -1) {
                if (this.target.name === '') {
                    this.target.name = this.queryString.substring(index + 5);
                    if (!this.isBulkSearch()) {
                        this.queryString = this.queryString.substring(0, index + 1);
                    }
                }
            }
        }
    }

    getSearchInfoFromTask(searchHandle: string, isOcrSearch = false): void {
        this.restService.post(Endpoint.taskManager_getTasks, {
            taskHandles: [searchHandle],
            includeDrDebug: true,
            includeResourceStatistics: true
        }).then(response => {
            if (response.tasks && response.tasks.length > 0) {
                if (this.currentSearch) {
                    if ((this.currentSearch.task === SearchType.FREEFORM) || (this.currentSearch.task === SearchType.SEARCH_FROM_BULK_SEARCH)) {
                        const queryEntered = _.findWhere(response.tasks[0].attributes, { name: 'queryEntered' });
                        if (!isUndefined(queryEntered)) {
                            this.setType(this.parseXMLQuerySearchType(queryEntered.value), true);
                        }
                        if (isOcrSearch || (this.target && this.target.name === '')) {
                            this.getSearchTarget({ attributes: response.tasks[0].attributes });
                        }
                        const corporaFilters = response.tasks[0].attributes.filter(a => a.name === 'filterHandles');
                        if (corporaFilters && corporaFilters.length > 0) {
                            this.restService.post(Endpoint.workingSetManager_listWorkingSetCorpora, {
                                handle: this.commonService.projectData.handle
                            }).then(responseCorpora => {
                                if (responseCorpora.corpora && responseCorpora.corpora.length > 0) {
                                    const corporaHandle = corporaFilters[0].value;
                                    const corporaHandle1 = corporaHandle.split(',');
                                    corporaHandle1.forEach(handle => {
                                        const filteredCorpora = _.filter(responseCorpora.corpora, (corpora) => corpora.corpusViewHandle === handle);
                                        if (filteredCorpora && filteredCorpora.length > 0) {
                                            this.corporaFilters.push(filteredCorpora[0]);
                                        }
                                    });
                                    const names = this.corporaFilters.map(function (elem) { return '- ' + elem.name; });
                                    this.corporaFilterTooltip = names.length > 1 ? 'Filtered by Data Sets:\n' + names.join('\n') : 'Filtered by Data Set:\n' + names;
                                }
                            });
                        }
                    } else {
                        this.setType(this.currentSearch.task);
                        if (this.currentSearch.task === SearchType.BULK && this.bulkSearchComponent) {
                            const bulkAttributes = response.tasks[0].attributes;
                            const combinedOperator = bulkAttributes.find(a => a.name === 'combineClauseType');
                            if (combinedOperator) {
                                this.bulkSearchComponent.runAsCombinedSearch = true;
                                this.bulkSearchComponent.selectedCombineOperator = combinedOperator.value;
                            }
                            const bulkDirectoryPath = bulkAttributes.find(a => a.name === 'bulkSearchDirectoryPath');
                            const bulkFileName = bulkAttributes.find(a => a.name === 'filename');
                            if (bulkDirectoryPath && bulkFileName) {
                                this.bulkSearchComponent.hasConnectorPath = true;
                                this.bulkSearchComponent.selectedConnectorPath = `${bulkDirectoryPath.value}/${bulkFileName.value}`;
                            }
                        }
                    }
                }
                if (this.target) {
                    const attrib = response.tasks[0].attributes.find(a => a.name === 'targetsize');
                    this.target.documentCount = attrib ? attrib.value : '';
                }
                const isWorkflowSearchResult = response.tasks[0].attributes && response.tasks[0].attributes.find(a => a.name === 'workflow_name');
                this.setSearchOptionsFromAttribs(response.tasks[0].attributes, isWorkflowSearchResult);
            }
        });
    }

    // isWorkflowStepOrSearchResult is needed because generateReport attributes comes with value always true from backend, TO BE REMOVED after backend will be fixed
    setSearchOptionsFromAttribs(searchTaskAttribs: any, isWorkflowStepOrSearchResult = false): void {
        if (searchTaskAttribs) {
            const familyAttrib = searchTaskAttribs.find(a => a.name === 'familyExpansion' || a.name === 'includeFamily');
            const metadataAttrib = searchTaskAttribs.find(a => a.name === 'metadataExpansion' || a.name === 'includeMetadata');
            const synonymAttrib = searchTaskAttribs.find(a => a.name === 'synonymExpansion' || a.name === 'includeSynonyms');
            const reportsAttrib = searchTaskAttribs.find(a => a.name === 'generateReport');

            const toggleControl = this.searchType === SearchType.FREEFORM ? this.freeFormToggles :
                (this.searchType === SearchType.ADVANCED ? this.advancedToggles : this.bulkSearchComponent.searchOptions);

            const familyCheckbox = toggleControl.find((item) => item.label === 'Include Families');
            if (familyCheckbox) {
                familyCheckbox.isChecked = familyAttrib ? familyAttrib.value === 'true' : false;
            }
            const metadataCheckbox = toggleControl.find((item) => item.label === 'Include Metadata');
            if (metadataCheckbox) {
                metadataCheckbox.isDisabled = this.searchType === SearchType.METADATA_BULK;
                metadataCheckbox.isChecked = metadataAttrib ? metadataAttrib.value === 'true' : ((this.searchType === SearchType.METADATA_BULK) ? true : false);
            }
            const expandCheckbox = toggleControl.find((item) => item.label === 'Expand Synonyms');
            if (expandCheckbox) {
                expandCheckbox.isDisabled = this.searchType === SearchType.METADATA_BULK;
                expandCheckbox.isChecked = synonymAttrib ? synonymAttrib.value === 'true' : false;
            }
            const repoCheckbox = toggleControl.find((item) => item.label === 'Generate Reports');
            if (repoCheckbox) {
                repoCheckbox.isDisabled = this.searchType === SearchType.METADATA_BULK;
                repoCheckbox.isChecked = reportsAttrib && !isWorkflowStepOrSearchResult ? reportsAttrib.value === 'true' : false;
            }
        }
    }

    setSearchResultAsTarget(): void {
        this.restService.post(Endpoint.projectManager_listSearchHistory, {
            projectHandle: this.commonService.currentProject.handle,
            startIndex: 0,
            count: 1
        }).then(response => {
            if (response.views && response.views.length > 0) {
                this.target = {
                    name: response.views[0].name,
                    handle: response.views[0].handle,
                    documentCount: response.views[0].documentCount
                };
            }
        });
    }

    getDefaultSearchTargetWithPermissions(): any {
        if (this.commonService.projectData) {
            if (Permissions.checkUserPermission(ObjectActionListType.ECA_CASE_DATA)) {
                this.target = {
                    name: 'Project Data',
                    handle: this.commonService.projectData.handle
                };
            } else {
                const entityPermissions = [ObjectActionListType.CORPUS, ObjectActionListType.SAVED_SEARCH_QUERY, ObjectActionListType.WORK_FLOW];
                let permissionMatched = false;
                for (const key in entityPermissions) {
                    const endpoint = entityPermissions[key] === ObjectActionListType.SAVED_SEARCH_QUERY ? Endpoint.projectManager_listSavedSearches : Endpoint.workFlowManager_getWorkFlows;
                    const responseName = entityPermissions[key] === ObjectActionListType.SAVED_SEARCH_QUERY ? 'activeRules' : 'workFlows';
                    if (Permissions.checkUserPermission(entityPermissions[key])) {
                        permissionMatched = true;
                        switch (entityPermissions[key]) {
                            case ObjectActionListType.CORPUS:
                                this.target = {
                                    name: 'Imports',
                                    handle: this.commonService.importsHandle
                                };
                                break;
                            case ObjectActionListType.SAVED_SEARCH_QUERY:
                            case ObjectActionListType.WORK_FLOW:
                                this.restService.post(endpoint, {
                                    projectHandle: this.commonService.currentProject.handle,
                                    startIndex: 0,
                                    count: 1
                                }).then(response => {
                                    const list = response[responseName];
                                    if (list && list.length > 0) {
                                        this.target = {
                                            name: list[0].name,
                                            handle: list[0].handle
                                        };
                                    } else {
                                        this.setSearchResultAsTarget();
                                    }
                                }, () => {
                                    this.setSearchResultAsTarget();
                                });
                                break;
                        }
                        break;
                    }
                }
                if (!this.target.name && !permissionMatched) {
                    this.setSearchResultAsTarget();
                }

            }
        }
    }

    setType(type: SearchType, keepSelection = false): void {
        if (type) {
            setTimeout(() => {
                this.searchType = type;
                const text = this.returnQueryStringToDisplay(this.queryString);
                this.genericInputComponent?.setText(text);
                this.genericInputComponent.colorize();
                this.checkWidth();
                const resetSearchSize = type === SearchType.FREEFORM ? true : false;
                this.searchTypeChangedEvent.emit({ searchType: type, keepSelection: keepSelection, resetSearchSize: resetSearchSize });
            });
        }
    }

    isBulkSearch(): boolean {
        return this.searchType === SearchType.BULK || this.searchType === SearchType.METADATA_BULK;
    }

    isSearchDisabled(): boolean {
        if (this.isBulkSearch()) {
            if (!isNullOrUndefined(this.bulkSearchComponent)) {
                if (this.workflowMode) {
                    return !this.bulkSearchComponent.isValidateEnabled();
                } else {
                    return !this.bulkSearchComponent.isBulkQueryValid;
                }
            } else {
                return true;
            }
        } else if (this.searchType === SearchType.ADVANCED) {
            return this.isNewQueryDisabled();
        } else {
            return isNullOrUndefined(this.queryString) || this.queryString.trim().length === 0;
        }
    }

    isNewQueryDisabled(): boolean {
        return !(this.advancedQuery && this.advancedQuery.query && this.advancedQuery.query.length > 0);
    }

    keyPressEvent(event) {
        if (event.key === 'Enter' && event.keyCode === 13) {
            if (this.genericInputComponent.suggestionListEnterClick) {
                this.genericInputComponent.suggestionListEnterClick = false;
            } else {
                this.search();
            }
        }
    }

    getQueryForWorkflow(): Promise<any> {
        return new Promise<any>((resolve) => {
            const searchStep = {};
            if (this.isBulkSearch()) {
                let requestData;
                Object.assign(searchStep, {
                    searchType: SearchType.BULK
                });
                if (this.bulkSearchComponent.isMetadataSearch) {
                    requestData = this.bulkSearchComponent.getMetadataSearchRequest(true);
                    Object.assign(searchStep, requestData);
                    Object.assign(searchStep, { searchType: SearchType.METADATA_BULK });
                } else if (this.bulkSearchComponent.hasConnectorPath) {
                    requestData = this.bulkSearchComponent.getBulkSearchRequestData();
                } else {
                    requestData = this.bulkSearchComponent.getBulkSearchRequestData(false, true);
                    Object.assign(searchStep, {
                        searchType: SearchType.BULK,
                        query: requestData.query,
                        expandFields: requestData.expandFields,
                        includeSynonyms: false,
                        includeFamilyMembers: requestData.includeFamilyMembers,
                        reportRequests: this.bulkSearchComponent.searchOptions.find((item) => item.label === 'Generate Reports')?.isChecked ? [{reportType: 'TOTAL_SEARCH_HIT_COUNT'}] : []
                    });
                }
            } else if (this.searchType === SearchType.ADVANCED) {
                Object.assign(searchStep, {
                    searchType: this.searchType,
                    query: this.advancedQuery,
                    includeFamilyMembers: this.advancedToggles.find((item) => item.label === 'Include Families').isChecked,
                    expandFields: this.advancedToggles.find((item) => item.label === 'Include Metadata').isChecked,
                    includeSynonyms: this.advancedToggles.find((item) => item.label === 'Expand Synonyms').isChecked,
                    reportRequests: this.advancedToggles.find((item) => item.label === 'Generate Reports')?.isChecked ? [{reportType: 'TOTAL_SEARCH_HIT_COUNT'}] : [],
                });
            } else {
                Object.assign(searchStep, {
                    searchType: this.searchType,
                    query: this.queryString.trim(),
                    includeFamilyMembers: this.freeFormToggles.find((item) => item.label === 'Include Families').isChecked,
                    expandFields: this.freeFormToggles.find((item) => item.label === 'Include Metadata').isChecked,
                    includeSynonyms: false,
                    reportRequests: this.freeFormToggles.find((item) => item.label === 'Generate Reports')?.isChecked ? [{reportType: 'TOTAL_SEARCH_HIT_COUNT'}] : []
                });
            }
            resolve(searchStep);
        });
    }

    search(): void {
        if (this.workflowMode || (this.searchType === SearchType.FREEFORM && (isNullOrUndefined(this.queryString) || this.queryString.trim().length === 0))) {
            return;
        }

        if (this.searchType === SearchType.FREEFORM || this.searchType === SearchType.ADVANCED) {
            const query = this.searchType === SearchType.FREEFORM ? this.queryString : this.advancedQuery.query.trim();
            const invalidMetadataResults = this.metadataService.findInvalidMetadataFields(query, this.metadataList, SettingsType.project);

            if (invalidMetadataResults.length > 0) {
                const firstInvalid = invalidMetadataResults[0];
                this.commonService.openErrorModal(`Invalid metadata field \"${firstInvalid.fullMatch}\"`);
                return;
            }
        }

        this.searchExec();
    }

    searchExec(): void {
        this.searchBusyMessage = 'Searching...';
        this.isSearchBusy = true;
        if (this.searchType === SearchType.FREEFORM) {
            this.capitalize();
            const xmlQuery = this.xmlService.stringToXMLQuery(this.queryString.replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ' ').trim(), this.isExpanded);
            this.restService.post(Endpoint.viewManager_search, {
                viewHandleList: [this.target.handle],
                query: xmlQuery,
                includeFamilyMembers: this.freeFormToggles.find((item) => item.label === 'Include Families').isChecked,
                expandFields: this.freeFormToggles.find((item) => item.label === 'Include Metadata').isChecked,
                includeSynonyms: false,
                taskDescription: '(' + this.queryString.trim() + ') in ' + this.target.name,
                attributes: [{ name: 'searchDialect', value: this.commonService.searchDialect }],
                reportRequests: this.freeFormToggles[2].isChecked ? this.getReportRequests() : [],
                corporaFilters: this.corporaFilters ? this.corporaFilters.map(function (elem) { return elem.handle; }) : []
            }).then(() => {
                this.searchCompleteUpdateActions();
                this.setCursorAtIndex();
            }, error => {
                this.isSearchBusy = false;
                this.genericInputComponent.colorize();
            });
        } else if (this.searchType === SearchType.ADVANCED) {
            this.queryString = this.advancedQuery.query.trim();
            const xmlQuery = this.advancedQuery.xmlQuery.replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ''); // this.xmlService.stringToXMLQuery(this.queryString, this.isExpanded, false, false);
            this.advancedQuery.queryObject = null;
            this.restService.post(Endpoint.viewManager_search, {
                viewHandleList: [this.target.handle],
                query: xmlQuery,
                includeFamilyMembers: this.advancedToggles.find((item) => item.label === 'Include Families').isChecked,
                expandFields: this.advancedToggles.find((item) => item.label === 'Include Metadata').isChecked,
                includeSynonyms: this.advancedToggles.find((item) => item.label === 'Expand Synonyms').isChecked,
                taskDescription: this.queryString.trim() + ' in ' + this.target.name,
                reportRequests: this.advancedToggles[3].isChecked ? this.getReportRequests() : [],
                corporaFilters: this.corporaFilters ? this.corporaFilters.map(function (elem) { return elem.handle; }) : []
            }).then(() => {
                this.searchCompleteUpdateActions();
            }, error => {
                this.isSearchBusy = false;
            });
        } else if (this.isBulkSearch()) {
            this.bulkSearchComponent.closePopovers();
            if (this.bulkSearchComponent.isMetadataSearch) {
                const requestData = this.bulkSearchComponent.getMetadataSearchRequest();
                requestData.dataAreaHandle = this.dataAreaHandle;
                if (requestData) {
                    requestData.targetViewHandle = this.target.handle;
                    requestData.taskDescription += this.target.name;
                    this.restService.post(Endpoint.viewManager_metaDataBulkSearch, requestData).then(() => {
                        this.searchCompleteUpdateActions();
                    }, error => {
                        this.isSearchBusy = false;
                    });
                }
            } else {
                const getBulkSearchRequestData = this.bulkSearchComponent.getBulkSearchRequestData();
                if (this.bulkSearchComponent.hasConnectorPath) {
                    getBulkSearchRequestData.dataAreaHandle = this.dataAreaHandle;
                }
                this.performBulkSearch(getBulkSearchRequestData);
            }
        }
        setTimeout(() => {
            if (this.isSearchBusy) {
                this.isSearchBusy = false;
            }
        }, 2000);
    }

    searchCompleteUpdateActions(): void {
        this.isSearchBusy = false;
    }
    performBulkSearch(requestData: any): void {
        if (requestData) {
            if (this.bulkSearchComponent.searchOptions[3].isChecked) {
                // generate reports is checked, add the report requests
                requestData.reportRequests = this.getReportRequests();
            }
            requestData = this.addTargetDataToBulkRequest(requestData);
            this.isSearchBusy = true;
            let searchEndpoint = Endpoint.viewManager_bulkSearch;
            if (this.bulkSearchComponent.runAsCombinedSearch && !this.bulkSearchComponent.hasConnectorPath) {
                searchEndpoint = Endpoint.viewManager_search;
            }
            this.restService.post(searchEndpoint, requestData).then(() => {
                this.searchCompleteUpdateActions();
                if (searchEndpoint === Endpoint.viewManager_search) {
                    this.bulkSearchComponent.clearAllQueries(true);
                }
            }, error => {
                this.isSearchBusy = false;
            });
        }
    }

    getReportRequests(): Array<any> {
        const reportsForSearch = [];
        reportsForSearch.push({ cachedOnly: false, reportType: 'OCR_CONFIDENCE_LEVEL' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_PRIMARY', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'OWNER' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TECHNICAL_REDUCTION' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'FILETYPE', attributes: [{ name: 'consolidated', value: 'true' }] });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DOCUMENT_CLASS' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'CORPUS_SOURCE' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'LANGUAGE', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DOMINANT_LANGUAGE', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'GROUP_VIEW_MEMBERSHIP', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TERM_SEARCH_HIT_COUNT' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'MAG_HIT_COUNT' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'SEARCH' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TAG_SUMMARY', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_SENDER', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_RECEIVER', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_SENT_DOMAINS', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_RECEIVED_DOMAINS', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_EMAIL_RECEIVED', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_EMAIL_SENT', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TOTAL_SEARCH_HIT_COUNT' })

        return reportsForSearch;
    }

    checkWidth(): void {
        if (this.elemRef && this.elemRef.nativeElement && this.elemRef.nativeElement.children && this.elemRef.nativeElement.children[0]) {
            this.totalWidth = this.elemRef.nativeElement.children[0].clientWidth;
            this.toggleWidth = 31;
            if (this.workflowMode) {
                this.toggleWidth = null;
            } else if (this.searchType === SearchType.ADVANCED) {
                if (this.totalWidth >= 1300) {
                    this.toggleWidth = 590;
                } else if (this.totalWidth >= 1135) {
                    this.toggleWidth = 473;
                } else if (this.totalWidth >= 945) {
                    this.toggleWidth = 320;
                } else if (this.totalWidth >= 802) {
                    this.toggleWidth = 172;
                }
            } else if (this.searchType === SearchType.FREEFORM) {
                if (this.totalWidth >= 1065) {
                    this.toggleWidth = 439;
                } else if (this.totalWidth >= 945) {
                    this.toggleWidth = 320;
                } else if (this.totalWidth >= 802) {
                    this.toggleWidth = 172;
                }
            }
        }
        if (this.searchType === SearchType.FREEFORM) {
            this.autosizeFreeform();
        }
    }

    selectTargetFullLabelVisible(): boolean {
        if (this.searchType === SearchType.FREEFORM) {
            return this.totalWidth >= 1230;
        }
        if (this.searchType === SearchType.ADVANCED) {
            return this.totalWidth >= 1382;
        }
        if (this.isBulkSearch()) {
            return this.totalWidth >= 904;
        }
    }

    wideButtons(): boolean {
        return this.totalWidth >= 1000;
    }

    widestButtons(): boolean {
        return this.totalWidth >= 800;
    }

    addTargetDataToBulkRequest(requestData: any, isValidateRequest = false): any {
        if (requestData && this.target) {
            requestData.attributes[0].value = this.target.handle;
            requestData.attributes[1].value = this.target.name;
            requestData.viewHandleList.push(this.target.handle);
            if (requestData.taskDescription && !isValidateRequest) {
                requestData.taskDescription += this.target.name;
            }
            requestData.corporaFilters = this.corporaFilters ? this.corporaFilters.map(function (elem) { return elem.handle; }) : [];
        }
        return requestData;
    }

    validateBulkQueries(): void {
        this.bulkSearchComponent.closePopovers();
        const getBulkSearchRequestData = this.bulkSearchComponent.getBulkSearchRequestData(true, this.workflowMode);
        this.validateBulkSearch(getBulkSearchRequestData);
    }

    validateBulkSearch(requestData: any): void {
        if (requestData && this.target) {
            requestData = this.addTargetDataToBulkRequest(requestData, true);
            this.bulkSearchComponent.isBulkQueryValid = false;
            this.isSearchBusy = true;
            this.searchBusyMessage = 'Validating Bulk Search Query...';
            this.restService.post(Endpoint.viewManager_validateBulkSearch, requestData).then(response => {
                if (response && response.handle) {
                    this.restService.post(Endpoint.viewManager_getValidateBulkSearchResult, { taskHandle: response.handle }).then(validateResult => {
                        if (validateResult) {
                            this.bulkSearchComponent.isBulkQueryValid = validateResult.numberOfErrors === 0 && validateResult.numberOfWarnings === 0;
                            this.bulkSearchComponent.showValidationResult(validateResult);
                            this.dataAreaHandle = validateResult.dataAreaHandle ? validateResult.dataAreaHandle : '';
                            this.bulkSearchComponent.bulkSearchFilePath = validateResult?.bulkSearchFilePath;
                        }
                        this.isSearchBusy = false;
                    }, error => {
                        this.isSearchBusy = false;
                    });
                }
            }, error => {
                this.isSearchBusy = false;
            });
        }
    }

    inputChanged(event?: any): void {
        setTimeout(() => {
            if (event?.source === 'user') {
                const deleteClipBoardEventTriggered = event.delta?.ops[0]?.delete || event.delta?.ops[1]?.delete;
                if (/\s/.test(event.delta?.ops[1]?.insert) || /\s/.test(event.delta?.ops[0]?.insert) || deleteClipBoardEventTriggered) {
                    const range = this.genericInputComponent?.getSelection();
                    if (deleteClipBoardEventTriggered && this.genericInputComponent.getText().trim().length === 0) {
                        this.queryString = event.text.trim();
                    } else {
                        this.genericInputComponent?.setText(this.genericInputComponent.capitalizeKeywords(this.genericInputComponent?.getText()));
                    }
                    this.genericInputComponent?.setSelection(range);
                }
                this.genericInputComponent.colorize();
            }
            this.renderFreeformSearchFromWorkflow(this.isQueryStringDisplayedTrimmed);
            if (!this.isQueryStringDisplayedTrimmed) {
                this.queryString = this.genericInputComponent.getText();
            }
            this.autosizeFreeform();
        });
    }

    capitalize(): void {
        this.genericInputComponent.setText(this.genericInputComponent.capitalizeKeywords(this.genericInputComponent.getText()));
        if (!this.isQueryStringDisplayedTrimmed) {
            const text = this.genericInputComponent?.getText();
            this.queryString = text;
        }
    }

    autosizeFreeform(): void {
        const content = this.genericInputComponent?.getText() as string;
        if (content?.length && content !== '\n') {
            if (!this.isQueryStringDisplayedTrimmed) {
                this.queryString = content.trim().replace(/[\u00a0\n\r]/g, ' ');
            }
            const editorEl = this.genericInputComponent?.getEditorElement();
            if (editorEl) {
                const queryRow = editorEl.closest('.query-row') as HTMLElement;
                if (queryRow && queryRow.scrollHeight > queryRow.clientHeight) {
                    const multiplier = Math.ceil(queryRow.scrollHeight / queryRow.clientHeight);
                    setTimeout(() => { this.searchTypeChangedEvent.emit({ searchType: this.searchType, offset: multiplier < 2 ? 20 : 20 * multiplier }); });
                }
            }
        }
    }

    autosizeFreeformDelayed(): void {
        setTimeout(() => { this.autosizeFreeform(); });
    }

    openHelp = (): void => {
        let helpIdx;
        if (this.searchType === SearchType.FREEFORM) {
            helpIdx = HelpIndex.myedisc_simple_search_dlg_dlg;
        } else if (this.isBulkSearch()) {
            helpIdx = HelpIndex.myedisc_bulk_search_dlg;
        } else if (this.searchType === SearchType.ADVANCED) {
            helpIdx = HelpIndex.myedisc_advanced_search;
        }
        this.commonService.openHelp(helpIdx);
    }

    editQuery(): void {
        this.advanceQueryModal(this.advancedQuery);
    }

    newQuery(): void {
        this.advanceQueryModal(new AdvancedSearchBundle());
    }

    advanceQueryModal(advancedSearchBundle: AdvancedSearchBundle): void {
        this.commonService.openModal(AdvancedSearchModalComponent, {
            modalData: {
                advancedQuery: advancedSearchBundle,
                selectedNode: this.target,
                isAdvanceSearchFromWorkFlow: this.workflowMode
            },
            class: 'vertical-percent-100 horizontal-percent-100'
        }).then(response => {
            if (response && response.modalResult && response.modalData) {
                if (response.modalData.editFreeform) {
                    this.queryString = response.modalData.query.trim();
                    this.setType(SearchType.FREEFORM);
                    this.freeFormToggles.find((item) => item.label === 'Include Families').isChecked = this.advancedToggles.find((item) => item.label === 'Include Families').isChecked;
                    this.freeFormToggles.find((item) => item.label === 'Include Metadata').isChecked = this.advancedToggles.find((item) => item.label === 'Include Metadata').isChecked;
                    this.freeFormToggles.find((item) => item.label === 'Generate Reports').isChecked = this.advancedToggles.find((item) => item.label === 'Generate Reports').isChecked;
                } else {
                    this.advancedQuery = response.modalData;
                }
            }
        });
    }

    clearAdvanceQuery(): void {
        this.advancedQuery = { query: '', xmlQuery: '', queryObject: '' };
    }

    freeformSearchRedirect(): void {
        this.queryString = this.advancedQuery.query.trim();
        this.setType(SearchType.FREEFORM);
        this.autosizeFreeformDelayed();
        this.setCursorAtIndex();
    }


    hideBusySpinner(): void {
        if (this.isSearchBusy) {
            this.isSearchBusy = false;
        }
    }

    returnQueryStringToDisplay(value: string): string {
        if (value?.length > MAX_LIMIT_OF_RENDERED_QUERY) {
            this.isQueryStringDisplayedTrimmed = true;
            return value.slice(0, MAX_LIMIT_OF_RENDERED_QUERY) + ' ...';
        } else {
            this.isQueryStringDisplayedTrimmed = false;
        }
        return value;
    }

    renderFreeformSearchFromWorkflow(isTextTrimmed: boolean): void {
        if (isTextTrimmed) {
            this.genericInputComponent?.disable();
        } else {
            this.genericInputComponent?.enable();
        }
    }

    freeformSearchExceedsLimitAlert(): void {
        if (this.isQueryStringDisplayedTrimmed) {
            this.commonService.openDiscardConfirmationModal('This query exceeds 4k characters. It has been truncated and cannot be edited here.', 'Alert', '', 'Close');
        }
    }

    ngOnDestroy(): void {
        this.cancelBulkSearch.unsubscribe();
    }
}
